import { useState, useEffect, useCallback } from 'react';
import { ForecastModel } from '@app/common/static-data/models';
import { VariableId, Location } from '../types';
import Logger from '../../../utils/logger';

interface ForecastState {
  selectedParam: VariableId;
  selectedModels: ForecastModel[];
  endHour: number;
  location: Location;
}

const STORAGE_KEY = 'forecastState';

const DEFAULT_STATE: ForecastState = {
  selectedParam: '2t',
  selectedModels: [],
  endHour: 480,
  location: {
    lat: 51.5074,
    lon: -0.1278,
    name: 'London, United Kingdom',
  },
};

export function useForecastStateCache() {
  const [state, setState] = useState<ForecastState>(DEFAULT_STATE);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load state from localStorage on mount
  useEffect(() => {
    try {
      const savedState = localStorage.getItem(STORAGE_KEY);
      if (savedState) {
        const parsedState = JSON.parse(savedState) as ForecastState;

        // Validate the parsed state has all required fields
        const validatedState: ForecastState = {
          selectedParam: parsedState.selectedParam || DEFAULT_STATE.selectedParam,
          selectedModels: Array.isArray(parsedState.selectedModels)
            ? parsedState.selectedModels
            : DEFAULT_STATE.selectedModels,
          endHour:
            typeof parsedState.endHour === 'number' ? parsedState.endHour : DEFAULT_STATE.endHour,
          location:
            parsedState.location && typeof parsedState.location === 'object'
              ? {
                  lat:
                    typeof parsedState.location.lat === 'number'
                      ? parsedState.location.lat
                      : DEFAULT_STATE.location.lat,
                  lon:
                    typeof parsedState.location.lon === 'number'
                      ? parsedState.location.lon
                      : DEFAULT_STATE.location.lon,
                  name:
                    typeof parsedState.location.name === 'string'
                      ? parsedState.location.name
                      : DEFAULT_STATE.location.name,
                }
              : DEFAULT_STATE.location,
        };

        setState(validatedState);
        Logger.info('Loaded forecast state from cache', validatedState);
      }
    } catch (error) {
      Logger.error('Failed to load forecast state from cache', error);
      setState(DEFAULT_STATE);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  // Save state to localStorage whenever it changes
  const saveState = useCallback((newState: ForecastState) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
      setState(newState);
      Logger.info('Saved forecast state to cache');
    } catch (error) {
      Logger.error('Failed to save forecast state to cache', error);
    }
  }, []);

  // Individual setters that update the cache
  const setSelectedParam = useCallback(
    (param: VariableId) => {
      const newState = { ...state, selectedParam: param };
      saveState(newState);
    },
    [state, saveState],
  );

  const setSelectedModels = useCallback(
    (models: ForecastModel[]) => {
      const newState = { ...state, selectedModels: models };
      saveState(newState);
    },
    [state, saveState],
  );

  const setEndHour = useCallback(
    (hour: number) => {
      const newState = { ...state, endHour: hour };
      saveState(newState);
    },
    [state, saveState],
  );

  const setLocation = useCallback(
    (location: Location) => {
      const newState = { ...state, location };
      saveState(newState);
    },
    [state, saveState],
  );

  // Bulk update function
  const updateState = useCallback(
    (updates: Partial<ForecastState>) => {
      const newState = { ...state, ...updates };
      saveState(newState);
    },
    [state, saveState],
  );

  // Clear cache function
  const clearCache = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setState(DEFAULT_STATE);
      Logger.info('Cleared forecast state cache');
    } catch (error) {
      Logger.error('Failed to clear forecast state cache', error);
    }
  }, []);

  return {
    // State
    state,
    isLoaded,

    // Individual setters
    setSelectedParam,
    setSelectedModels,
    setEndHour,
    setLocation,

    // Bulk operations
    updateState,
    clearCache,

    // Direct access to state properties
    selectedParam: state.selectedParam,
    selectedModels: state.selectedModels,
    endHour: state.endHour,
    location: state.location,
  };
}
